(function () {
  'use strict';

  const responseCache = new Map();

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function uniqueIds(values) {
    return [...new Set((values || []).map(String).filter(Boolean))];
  }

  async function fetchText(url, signal) {
    if (responseCache.has(url)) {
      return responseCache.get(url);
    }

    const response = await fetch(url, {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Recommendation request failed with status ${response.status}.`);
    }

    const text = await response.text();
    responseCache.set(url, text);
    return text;
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define(
      'cart-recommendations',
      class CartRecommendations extends HTMLElement {
        constructor() {
          super();
          this.abortController = null;
          this.handleClick = this.handleClick.bind(this);
        }

        connectedCallback() {
          this.addEventListener('click', this.handleClick);
          this.load();
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.handleClick);
          this.abortController?.abort();
        }

        readConfig() {
          const node = this.querySelector('[data-ls-cart-recommendation-config]');

          if (!node) return null;

          try {
            return JSON.parse(node.textContent || '{}');
          } catch (error) {
            console.error('[Love Smiths] Invalid cart recommendation configuration:', error);
            return null;
          }
        }

        buildUrl(productId, intent) {
          const sourceSection = this.dataset.sourceSection || 'ls-cart-recommendation-source';
          const params = new URLSearchParams({
            product_id: String(productId),
            limit: '10',
            intent,
            section_id: sourceSection,
          });

          return `${getRootUrl()}recommendations/products?${params.toString()}`;
        }

        async fetchCards(productId, intent) {
          const htmlText = await fetchText(
            this.buildUrl(productId, intent),
            this.abortController?.signal
          );
          const html = new DOMParser().parseFromString(htmlText, 'text/html');

          return [...html.querySelectorAll('[data-ls-recommendation-product]')];
        }

        addGroupsRoundRobin(groups, selectedNodes, selectedIds, maximumProducts) {
          const longestGroup = groups.reduce((maximum, group) => Math.max(maximum, group.length), 0);

          for (let position = 0; position < longestGroup; position += 1) {
            for (const group of groups) {
              if (selectedNodes.length >= maximumProducts) return;

              const node = group[position];
              if (!node) continue;

              const productId = String(node.dataset.productId || '');
              if (!productId || selectedIds.has(productId)) continue;

              selectedIds.add(productId);
              selectedNodes.push(document.importNode(node, true));
            }
          }
        }

        async collectIntent(intent, seedProductIds, selectedNodes, selectedIds, maximumProducts) {
          if (selectedNodes.length >= maximumProducts) return;

          const groups = await Promise.all(
            seedProductIds.map((productId) =>
              this.fetchCards(productId, intent).catch((error) => {
                if (error.name !== 'AbortError') {
                  console.error(`[Love Smiths] ${intent} recommendation error:`, error);
                }
                return [];
              })
            )
          );

          this.addGroupsRoundRobin(groups, selectedNodes, selectedIds, maximumProducts);
        }

        async load() {
          const config = this.readConfig();
          const content = this.querySelector('[data-ls-cart-recommendation-content]');
          const list = this.querySelector('[data-ls-cart-recommendation-list]');

          if (!config || !content || !list) {
            this.hidden = true;
            return;
          }

          const seedProductIds = uniqueIds(config.seedProductIds);
          const maximumProducts = Math.max(1, Math.min(Number(config.maximumProducts || 4), 4));

          if (!seedProductIds.length) {
            this.hidden = true;
            return;
          }

          this.abortController?.abort();
          this.abortController = new AbortController();
          this.hidden = false;
          content.hidden = true;
          list.innerHTML = '';

          const selectedNodes = [];
          const selectedIds = new Set(uniqueIds(config.cartProductIds));

          await this.collectIntent(
            'complementary',
            seedProductIds,
            selectedNodes,
            selectedIds,
            maximumProducts
          );

          if (config.useRelatedFallback && selectedNodes.length < maximumProducts) {
            await this.collectIntent(
              'related',
              seedProductIds,
              selectedNodes,
              selectedIds,
              maximumProducts
            );
          }

          if (!this.isConnected || this.abortController.signal.aborted) return;

          if (!selectedNodes.length) {
            this.hidden = true;
            return;
          }

          const fragment = document.createDocumentFragment();
          selectedNodes.slice(0, maximumProducts).forEach((node) => fragment.appendChild(node));
          list.appendChild(fragment);
          content.hidden = false;
        }

        async handleClick(event) {
          const button = event.target.closest('[data-ls-cart-recommendation-add]');

          if (!button || !this.contains(button)) return;

          event.preventDefault();

          const variantId = Number(button.dataset.variantId || 0);
          if (!variantId || button.disabled) return;

          const status = this.querySelector('[data-ls-cart-recommendation-status]');
          const config = this.readConfig() || {};
          const spinner = button.querySelector('.loading__spinner');

          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
          spinner?.classList.remove('hidden');

          try {
            if (!window.LSAjaxCart?.addItems) {
              throw new Error('The shared cart controller is unavailable.');
            }

            await window.LSAjaxCart.addItems(
              [{ id: variantId, quantity: 1 }],
              {
                open: false,
                trigger: button,
                source: 'ls-cart-recommendations',
              }
            );

            if (this.isConnected && status) {
              status.textContent = config.addedMessage || '';
            }
          } catch (error) {
            console.error('[Love Smiths] Recommendation add error:', error);
            if (status) status.textContent = config.errorMessage || window.cartStrings?.error || '';
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            button.classList.remove('loading');
            spinner?.classList.add('hidden');
          }
        }

      }
    );
  }
})();
